import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import SearchReports from "./searchReports";
import ReportsPagination from "./pagenation";
import { CircularProgress, Box, Button } from "@mui/material";
import { useNotification } from "../../context/NotificationContext";

export default function ProfitLossReport() {
  const { showNotification } = useNotification();
  const showNotificationRef = useRef(showNotification);
  const navigate = useNavigate();
  const sessionExpiredRef = useRef(false);

  // DATA
  const [plReports, setPlReports] = useState([]);

  // FILTERS
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [aggregator, setAggregator] = useState("");

  // PAGINATION
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // LOADING
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);

  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm]);

  /* --------------------------------
   * FETCH PROFIT & LOSS (SERVER SIDE)
   ---------------------------------*/
  const fetchProfitLoss = useCallback(async () => {
    if (sessionExpiredRef.current) return;
    setTableLoading(true);

    const params = new URLSearchParams({
      page: currentPage,
      limit: itemsPerPage,
      search: debouncedSearch,
      fromDate,
      toDate,
      aggregator,
    });

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKENDORDERURL}/reports/profit-loss?${params}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const json = await res.json();

      if (res.status === 401) {
        showNotificationRef.current(
          "Session expired. Please login again.",
          "error"
        );
        localStorage.removeItem("token");
        sessionExpiredRef.current = true;
        navigate("/login", { replace: true });
        return;
      }

      if (!res.ok || !json.success) {
        showNotificationRef.current("Failed to load report", "error");
        return;
      }

      setPlReports(json.data);
      setTotalPages(json.pagination.totalPages);
      setTotalItems(json.pagination.totalItems);
    } catch (err) {
      showNotificationRef.current(
        "Failed to load Profit & Loss report",
        "error"
      );
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  }, [
    currentPage,
    debouncedSearch,
    fromDate,
    toDate,
    aggregator,
    itemsPerPage,
    navigate,
  ]);

  useEffect(() => {
    fetchProfitLoss();
  }, [fetchProfitLoss]);
  /* --------------------------------
   * GENERATE PROFIT & LOSS (API-1)
   ---------------------------------*/
  const handleGeneratePL = async () => {
    try {
      setLoading(true);
      await fetch(
        `${process.env.REACT_APP_BACKENDORDERURL}/reports/profit-loss/generate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setCurrentPage(1);
      await fetchProfitLoss();
    } catch (e) {
      showNotification("Failed to generate report", "error");
    } finally {
      setLoading(false);
    }
  };

  /* --------------------------------
   * AGGREGATOR OPTIONS
   ---------------------------------*/
  const aggregators = Array.from(
    new Set(plReports.map((r) => r.aggregator).filter(Boolean))
  );

  /* --------------------------------
   * INITIAL LOADING
   ---------------------------------*/
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <CircularProgress size={64} />
      </Box>
    );
  }

  return (
    <section className="content-body">
      <div className="profit-loss-container">
        <div className="table-container">
          <h3>Profit &amp; Loss</h3>

          <div className="d-flex justify-space-between mb-4 gap-1">
            <SearchReports
              onSearch={setSearchTerm}
              onFromDate={setFromDate}
              onToDate={setToDate}
              fromDate={fromDate}
              toDate={toDate}
              aggregators={aggregators}
              aggregator={aggregator}
              onAggregatorChange={setAggregator}
              reports={plReports} // ✅ IMPORTANT
            />

            <Button variant="contained" onClick={handleGeneratePL}>
              Generate
            </Button>
          </div>

          <table className="data-table">
            <thead>
              <tr>
                <th>Order Date</th>
                <th>Aggregator</th>
                <th>Bill No</th>
                <th>Payment Status</th>
                <th>Order Status</th>
                <th>Order Value</th>
                <th>CBH Menu Value</th>
              </tr>
            </thead>

            <tbody>
              {tableLoading ? (
                <tr>
                  <td colSpan="7" align="center">
                    <CircularProgress size={28} />
                  </td>
                </tr>
              ) : plReports.length === 0 ? (
                <tr>
                  <td colSpan="7" align="center">
                    No reports found
                  </td>
                </tr>
              ) : (
                plReports.map((r) => (
                  <tr key={r._id}>
                    <td>{r.orderDate}</td>
                    <td>{r.aggregator}</td>
                    <td>{r.billNo}</td>
                    <td>{r.paymentStatus}</td>
                    <td>{r.status}</td>
                    <td>₹{r.totalAmount}</td>
                    <td>{r.menuAmount}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* PAGINATION */}
          {totalItems > 0 && (
            <ReportsPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
            />
          )}
        </div>
      </div>
    </section>
  );
}
